import { yupResolver } from '@hookform/resolvers/yup'
import React, { ChangeEvent, FunctionComponent, useEffect } from 'react'
import { Row, Col } from 'react-grid-system'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { number, object, string } from 'yup'

import { ClinTheme } from '../../../ClinTheme'
import { ClinSelect, ClinSelectVariant } from '../../../components/ClinSelect'
import { ClinSpacer } from '../../../components/ClinSpacer'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { ClinText } from '../../../components/ClinText'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import { ClinTextInput } from '../../../components/ClinTextInput'
import {
  CountryDto,
  CreateShippingAddressDto
} from '../../../types/swaggerTypes'
import {
  getInputPlaceholder,
  PlaceholderType
} from '../../../utils/Forms/getInputPlaceholder'
import {
  getInputValidation,
  ValidationType
} from '../../../utils/Forms/getInputValidation'
import {
  AnnounceMode,
  ClinAnnounceBar
} from '../../../components/ClinAnnounceBar/ClinAnnounceBar'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import { AnalyticsEvent } from '../../../services/Analytics'
import {
  StyledRow,
  StyledColumn,
  StyledLoadingContainer,
  StyledLoadingSpinner
} from './AccountDetailsEditUserForm.styles'
import {
  StyledClinPrimaryButton2,
  StyledClinSecondaryButton
} from './AccountDetails.styles'
import { ClinGroup } from '../../../components/ClinGroup'

interface IAccountDetailsAddressProps {
  /** Displays an error notification **/
  hasError?: boolean
  /** Displays error message **/
  addressErrorMessage?: string
  /** Whether the submission is still processing **/
  isSubmitting: boolean
  /** List of countries **/
  countries: CountryDto[]
  resetOnSubmit?: boolean
  selectedCountry?: string
  addressModalError?: string
  setIsRequestNewAddress?: (isNewAddress: boolean) => void
  //reset form manually - for be validation
  resetForm?: boolean
  /** Handle confirmation of dissociation  **/
  handleFormSubmit: (data: CreateShippingAddressDto) => void
  /** Support contact details **/
  supportContact: CountryDto
}

const defaultState: CreateShippingAddressDto = {
  address: {
    addressId: 0,
    address1: '',
    address2: '',
    address3: '',
    address4: '',
    city: '',
    postalCode: '',
    state: '',
    country: ''
  }
}

export const AccountDetailsAddressForm: FunctionComponent<
  IAccountDetailsAddressProps
> = ({
  countries,
  isSubmitting,
  setIsRequestNewAddress,
  selectedCountry,
  addressErrorMessage,
  resetForm,
  resetOnSubmit,
  handleFormSubmit,
  supportContact
}) => {
  const { t } = useTranslation()
  const requiredText = getInputValidation(ValidationType.RequiredField)

  const NewShippingAddressSchema = object().shape({
    address: object().shape({
      addressId: number(),
      address1: string().required(requiredText),
      address2: string().required(requiredText),
      address3: string(),
      address4: string(),
      city: string().required(requiredText),
      postalCode: string().required(requiredText),
      state: string().required(requiredText),
      country: string().required(requiredText)
    })
  })

  const {
    register,
    control,
    reset,
    setValue,
    clearErrors,
    handleSubmit,
    formState: { errors }
  } = useForm<CreateShippingAddressDto>({
    defaultValues: defaultState,
    resolver: yupResolver(NewShippingAddressSchema)
  })

  const onSubmit = handleSubmit((data: CreateShippingAddressDto) => {
    handleFormSubmit(data)
    clearErrors()
    resetOnSubmit && reset({})
  })

  const onCancel = () => {
    if (setIsRequestNewAddress) {
      setIsRequestNewAddress(false)
    }
    reset({})
  }
  useEffect(() => {
    if (selectedCountry) {
      setValue('address.country', selectedCountry)
    }
  }, [selectedCountry, setValue])

  useEffect(() => {
    if (resetForm) {
      reset(defaultState)
    }

    return () => {}
  }, [resetForm])

  return (
    <form onSubmit={onSubmit}>
      <Row>
        <Col xs={12}>
          <ClinText
            fontSize={ClinTheme.fontSizes[2]}
            lineHeight={ClinTheme.lineHeights.body}
            marginTop={0}
            marginBottom={0}
          >
            {t('request_new_shipping_address:description')}
          </ClinText>
          <ClinAnnounceBar
            mode={AnnounceMode.Information}
            marginTop={ClinTheme.space[3]}
            title={t('request_new_shipping_address:form_announce_title')}
          >
            <Trans
              i18nKey="request_new_shipping_address:form_announce_description"
              components={{
                phone: (
                  <a
                    aria-label="Phone number link to customer support"
                    href={`tel:${supportContact.rawPhoneNumber}`}
                    style={{ textDecoration: 'underline' }}
                  />
                ),
                email: (
                  <a
                    aria-label="Mail to customer support"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`mailto:${supportContact.csEmailAddress}`}
                    style={{ textDecoration: 'underline' }}
                    onClick={() => {
                      analyticsServiceSingleton.trackEvent(
                        AnalyticsEvent.ContactClicked,
                        { contactType: 'email' }
                      )
                    }}
                  />
                )
              }}
              values={{
                csPhoneNumber: supportContact.csPhoneNumber,
                csEmailAddress: supportContact.csEmailAddress
              }}
            >
              <></>
            </Trans>
          </ClinAnnounceBar>
          <ClinSpacer />
          <ClinText
            fontSize={ClinTheme.fontSizes[2]}
            lineHeight={ClinTheme.lineHeights.body}
            marginTop={0}
            marginBottom={0}
          >
            {t('request_new_shipping_address:mandatory_message')}
          </ClinText>
        </Col>
      </Row>
      <ClinSpacer height={ClinTheme.space[3]} />
      <Row>
        <Col xs={12}>
          <div style={{ display: 'none' }}>
            <ClinTextInput
              id="address-id"
              {...register(`address.addressId`)}
              value={0}
            />
          </div>
          <ClinTextInput
            id="hospital-or-institute"
            {...register(`address.address1`)}
            label={t('request_new_shipping_address:form_label_hospital')}
            width="100%"
            labelFontWeight={ClinTheme.fontWeights.normal}
            hasError={!!(errors && errors.address?.address1)}
            prompt={errors.address?.address1?.message}
          />
          <ClinSpacer height={ClinTheme.space[3]} />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <ClinTextInput
            id="address-one"
            {...register(`address.address2`)}
            label={t('request_new_shipping_address:form_label_address_line_1')}
            width="100%"
            labelFontWeight={ClinTheme.fontWeights.normal}
            hasError={!!(errors && errors.address?.address2)}
            prompt={errors.address?.address2?.message}
          />
          <ClinSpacer height={ClinTheme.space[3]} />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <ClinTextInput
            id="address-two"
            {...register(`address.address3`)}
            label={t('request_new_shipping_address:form_label_address_line_2')}
            width="100%"
            labelFontWeight={ClinTheme.fontWeights.normal}
            hasError={!!(errors && errors.address?.address3)}
            prompt={errors.address?.address3?.message}
          />
          <ClinSpacer height={ClinTheme.space[3]} />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <ClinTextInput
            id="address-three"
            {...register(`address.address4`)}
            label={t('request_new_shipping_address:form_label_address_line_3')}
            width="100%"
            labelFontWeight={ClinTheme.fontWeights.normal}
            hasError={!!(errors && errors.address?.address4)}
            prompt={errors.address?.address4?.message}
          />
          <ClinSpacer height={ClinTheme.space[3]} />
        </Col>
      </Row>
      <StyledRow>
        <StyledColumn width={195}>
          <ClinTextInput
            id="city"
            {...register(`address.city`)}
            label={t('request_new_shipping_address:form_label_city')}
            width="100%"
            labelFontWeight={ClinTheme.fontWeights.normal}
            hasError={!!(errors && errors.address?.city)}
            prompt={errors.address?.city?.message}
          />
        </StyledColumn>
        <StyledColumn width={195}>
          <ClinTextInput
            id="state-county-provence"
            {...register(`address.state`)}
            label={t('request_new_shipping_address:form_label_state')}
            width="100%"
            labelFontWeight={ClinTheme.fontWeights.normal}
            hasError={!!(errors && errors.address?.state)}
            prompt={errors.address?.state?.message}
          />
        </StyledColumn>
      </StyledRow>

      <ClinSpacer height={ClinTheme.space[3]} />

      <StyledRow>
        <StyledColumn width={270}>
          <Controller
            name={'address.country'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <ClinSelect
                id="country"
                label={t('request_new_shipping_address:form_label_country')}
                width="100%"
                variant={ClinSelectVariant.primaryV2}
                value={value}
                defaultValue={selectedCountry}
                disabled={selectedCountry ? true : false}
                onChange={(changeValue: ChangeEvent<HTMLSelectElement>) =>
                  onChange(changeValue.currentTarget.value)
                }
                hasError={!!(errors && errors.address?.country)}
                prompt={errors.address?.country?.message}
              >
                <option value="" disabled={true}>
                  {getInputPlaceholder(PlaceholderType.SelectInput)}
                </option>
                {countries
                  .sort((a, b) =>
                    a.countryName && b.countryName
                      ? a.countryName.localeCompare(b.countryName)
                      : -1
                  )
                  .map((country: CountryDto, index: number) => {
                    return (
                      <option
                        key={`country-codes-${index}`}
                        value={country.countryCode}
                      >
                        {country.countryName}
                      </option>
                    )
                  })}
              </ClinSelect>
            )}
          />
          {selectedCountry && (
            <ClinText marginTop={'5px'} variant={TypographyVariant.SmallUI}>
              {t('request_new_shipping_address:disabled_country_warning_text')}
            </ClinText>
          )}
        </StyledColumn>
        <StyledColumn width={120}>
          <ClinTextInput
            id="postal-code"
            {...register(`address.postalCode`)}
            label={t('request_new_shipping_address:form_label_postcode')}
            width="100%"
            labelFontWeight={ClinTheme.fontWeights.normal}
            hasError={!!(errors && errors.address?.postalCode)}
            prompt={errors.address?.postalCode?.message}
          />
        </StyledColumn>
      </StyledRow>

      {addressErrorMessage && (
        <>
          <ClinSpacer height={ClinTheme.space[3]} />
          <ClinText color="red" variant={TypographyVariant.SmallUI}>
            {addressErrorMessage}
          </ClinText>
        </>
      )}

      <ClinSpacer height={ClinTheme.space[5]} />

      <ClinGroup>
        <StyledClinSecondaryButton onClick={() => onCancel()}>
          {t('common:buttons.cancel')}
        </StyledClinSecondaryButton>
        <StyledLoadingContainer>
          <StyledClinPrimaryButton2
            type="submit"
            variant="primary"
            disabled={isSubmitting}
          >
            {t('request_new_shipping_address:save_request')}
          </StyledClinPrimaryButton2>
          {isSubmitting && (
            <StyledLoadingSpinner>
              <ClinSpinner />
            </StyledLoadingSpinner>
          )}
        </StyledLoadingContainer>
      </ClinGroup>
    </form>
  )
}
